import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { switchMap, tap, map, catchError, finalize } from 'rxjs/operators';
import { CabinetService } from 'src/app/cabinet/services/cabinet.service';
import { ICreateCabinetRequest, IExportCabinetRequest, IGetCabinetResponse, IUpdateCabinetRequest } from 'src/app/cabinet/models/cabinet';
import { IBaseGetByComanyRequest, IBaseSearchRequest, IPagedResults } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { Router } from '@angular/router';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';

export interface CabinetState {
  selectedCabinet: IGetCabinetResponse;
  cabinets: IPagedResults<IGetCabinetResponse[]>;
  loading: boolean;
  loadingExport: boolean;
}

@Injectable()
export class CabinetStore extends ComponentStore<CabinetState> {

  // Initial state
  constructor(private cabinetService: CabinetService,
    private appnotificationService: AppNotificationService,
    private router: Router
  ) {
    super({ selectedCabinet: null, cabinets: null, loading: false, loadingExport: false });
  }

  // Selectors
  readonly loading$ = this.select((state) => state.loading);
  readonly loadingExport$ = this.select((state) => state.loadingExport);
  readonly selectedCabinet$: Observable<IGetCabinetResponse> = this.select(state => state.selectedCabinet);
  readonly cabinets$: Observable<IPagedResults<IGetCabinetResponse[]>> = this.select(state => state.cabinets);


  // Effect to load cabinet by ID from the server
  readonly getCabinetById = this.effect<string>(cabinetId$ =>
    cabinetId$.pipe(
      switchMap(cabinetId =>
        this.cabinetService.getCabinetById(cabinetId).pipe(
          tap({
            next: (selectedCabinet) => this.patchState({ selectedCabinet }),
            error: (err) => console.error('Failed to load cabinet:', err)
          }),
          catchError((err) => {
            console.error('Failed to load cabinet:', err);
            return [];
          })
        )
      )
    )
  );

  // Effect to load cabinet by ID from the server
  readonly getCabinetsByCompany = this.effect((searchRequest$: Observable<IBaseGetByComanyRequest>) =>
    searchRequest$.pipe(
      switchMap(searchRequest =>
        this.cabinetService.getCabinetsByCompany(searchRequest).pipe(
          tap({
            next: (cabinets) => this.patchState({ cabinets }),
            error: (err) => console.error('Failed to load cabinets:', err)
          }),
          catchError((err) => {
            console.error('Failed to load cabinets:', err);
            return [];
          })
        )
      )
    )
  );

// Effect to update a cabinet by its ID
readonly updateCabinetById = this.effect<IUpdateCabinetRequest>(cabinet$ =>
  cabinet$.pipe(
    switchMap(cabinet =>
      this.cabinetService.updateCabinet(cabinet).pipe(
        tap({
          next: (updatedCabinet) => {
            if (updatedCabinet) {
              // Update the state with the updated cabinet
              this.patchState(state => ({
                selectedCabinet:
                  updatedCabinet.id === state.selectedCabinet?.id
                    ? updatedCabinet
                    : state.selectedCabinet,
                cabinets: {
                  ...state.cabinets,
                  results: state.cabinets?.data
                    ? state.cabinets.data.map(item =>
                        item.id === updatedCabinet.id ? updatedCabinet : item
                      )
                    : [] // Handle undefined or null state.cabinets.data
                }
              }));

              this.appnotificationService.notification$.next({
                message: translate('cabinets.updateSuccess'),
                style: 'success'
              });
            }
          }
        })
      )
    )
  )
);


  // Effect to create a new cabinet
  readonly createCabinet = this.effect<ICreateCabinetRequest>(cabinet$ =>
    cabinet$.pipe(
      switchMap(cabinet =>
        this.cabinetService.createCabinet(cabinet).pipe(
          tap({
            next: (createdCabinet) => {
              if (createdCabinet) {

                const currentUrl = this.router.url;
                
                // Remove the last two segments of url
                const segments = currentUrl.split('/');
                segments.splice(-1);

                // Rebuild the new URL
                const newUrl = segments.join('/') + '/' + createdCabinet.id

                // Navigate to the new URL
                this.router.navigateByUrl(newUrl).then(() => {
                  // Reload the page after navigation
                  window.location.reload();
                });

                // Update the state with the newly created cabinet
                this.patchState(state => ({
                  selectedCabinet: createdCabinet
                }));

                this.appnotificationService.notification$.next({
                  message: translate('cabinets.createSuccess'),
                  style: 'success'
                });
              }
            }
          })
        )
      )
    )
  );

  // Effect to delete cabinet by id
  readonly deleteCabinetById = this.effect<string>(cabinetId$ =>
    cabinetId$.pipe(
      switchMap(cabinetId =>
        this.cabinetService.deleteCabinet(cabinetId).pipe(
          tap({
            next: () => {
              // Redirect to company page
              const currentUrl = this.router.url;

              // Remove the last two segments of url
              const segments = currentUrl.split('/');
              segments.splice(-2);

              // Rebuild the new URL
              const newUrl = segments.join('/');

              // Navigate to the new URL
              this.router.navigateByUrl(newUrl);

              // Notify success
              this.appnotificationService.notification$.next({
                message: translate('cabinets.deleteSuccess'),
                style: 'success'
              });
            },
            error: (err) => {
              console.error('Failed to delete cabinet:', err);
            }
          }),
          catchError((err) => {
            console.error('Error during cabinet deletion:', err);
            return [];
          })
        )
      )
    )
  );

  // Effect to handle the export process
  readonly exportCabinet = this.effect<IExportCabinetRequest>(cabinetExport$ =>
    cabinetExport$.pipe(
      switchMap(cabinetExport =>
      this.cabinetService.exportCabinet({ id: cabinetExport.id, sorting: cabinetExport.sorting }).pipe(
        tap((response) => {
          downloadBlob(response, 'cabinet-export.xlsx'); // Trigger file download
          this.patchState({ loadingExport: false });
        }),
        catchError(() => {
          this.patchState({ loadingExport: false });
          return of(null); // Handle error silently
        }),
        finalize(() => {
          this.setLoading(false);
          this.patchState({ loadingExport: false });
        }) // Ensure loading is stopped
      )
    )
  )
  );
  

  // Updater to set the loading state
  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: undefined, // Clear error when starting a new process
  }));

}
